import type { Nullable } from '@src/shared/types/common';

/**
 * @module kendo-editor
 * @description Модуль для работы с редактором Kendo UI Editor.
 *
 * Предоставляет функциональность:
 * - Настройка горячих клавиш (Shift+Enter для разрыва строки, Enter для параграфа)
 * - Управление высотой редактора
 * - Сохранение комментариев
 * - Фокус в body редактора при событии select (edtBodyFocus)
 * - Сброс устаревшего выделения после команд цвета (Kendo bug в ColorTool)
 */
kendo.ui.Editor.defaultTools['insertLineBreak'].options.shift = false;
kendo.ui.Editor.defaultTools['insertParagraph'].options.shift = true;

const editorPrototype = kendo.ui.Editor.prototype;
const originalExec = editorPrototype.exec;

/**
 * Kendo bug: ColorTool (ForeColor/BackColor), в отличие от FontTool/LineHeightTool,
 * не сбрасывает сохранённый range (`_range`), и следующая команда — Ctrl+B или
 * кнопка Bold — восстанавливает выделение на ранее покрашенной строке.
 */
editorPrototype.exec = function (this: kendo.ui.Editor, name, params) {
    const result = originalExec.call(this, name, params);

    if (name === 'foreColor' || name === 'backColor') {
        this._deleteSavedRange?.();
    }

    return result;
};

/**
 * Сохраняет комментарий при нажатии на кнопку.
 * Триггерит клик на кнопке добавления комментария, если в редакторе есть текст.
 */
function saveCommentOnButton() {
    const editor = $('#iHasComments_tbComment').data('kendoEditor');
    const $addButton = $('#iHasComments_btAddComment');

    if (editor?.value() && $addButton.length) {
        $addButton.trigger('click');
    }
}

/**
 * Устанавливает высоту редактора на основе высоты содержимого iframe.
 * @param selector - jQuery элемент с iframe содержимым
 * @param editorSelector - jQuery элемент с редактором Kendo
 */
function setHeightContentEditor(selector: JQuery, editorSelector: JQuery) {
    const descriptionFrameHeight = selector.contents().height() ?? 0;
    editorSelector.find('.k-widget.k-editor').height(descriptionFrameHeight + 110);
}

/**
 * Обработчик события выполнения команды в редакторе Kendo.
 * При открытии режима просмотра HTML корректирует размер iframe.
 * @param event - Событие выполнения команды редактора
 */
function kendoEditor_onExecute({ name }: kendo.ui.EditorExecuteEvent) {
    if (name === 'viewhtml') {
        setTimeout(window.setProperIframeResize);
    }
}

/**
 * Обработчик события select редактора Kendo.
 * Переносит фокус в iframe body, чтобы сразу можно было вводить текст.
 * Используется в Razor: .Events(e => e.Select("edtBodyFocus")).
 * @param e - Событие select с экземпляром редактора в sender
 */
function edtBodyFocus(e: { sender: { body: HTMLElement } }) {
    e.sender.body.focus();
}

/**
 * Фиксирует размеры после ресайза.
 *
 * Проблема: Kendo вешает ResizeObserver на .k-editor-resizable и при любом
 * изменении размеров (в т.ч. при фокусе на других полях) вызывает внутренние
 * методы toolbar.resize() и _handleSpaceAndNewLines(), которые пересчитывают
 * высоту редактора, вызывая её скачки.
 *
 * Решение: после завершения пользовательского ресайза фиксируем высоту через
 * min-height + max-height. Это не даёт ResizeObserver изменить размеры.
 */
function initKendoEditorVerticalOnlyResize() {
    document.addEventListener('mousedown', (e) => {
        if (!(e.target instanceof Element)) {
            return;
        }

        const wrapper = e.target.closest<HTMLElement>('.k-editor-resizable');

        if (!wrapper) {
            return;
        }

        // Сбрасываем зафиксированные размеры, чтобы разблокировать повторный ресайз, устанавливаем минимальную высоту
        wrapper.style.minHeight = '90px';
        wrapper.style.maxHeight = '';

        // Накрываем iframe прозрачным overlay, чтобы события мыши не проваливались в него
        const iframe = wrapper.querySelector<HTMLElement>('iframe.k-content');
        let overlay: Nullable<HTMLDivElement> = null;

        if (iframe && iframe.parentElement) {
            overlay = document.createElement('div');
            overlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:9999;';
            iframe.parentElement.style.position = 'relative';
            iframe.parentElement.appendChild(overlay);
        }

        const cleanup = () => {
            // Убираем overlay после завершения ресайза
            overlay?.remove();
            overlay = null;

            const newHeight = wrapper.offsetHeight;

            // Устанавливаем фиксированную высоту
            wrapper.style.minHeight = `${newHeight}px`;
            wrapper.style.maxHeight = `${newHeight}px`;
        };

        document.addEventListener('mouseup', cleanup, { once: true });
    });
}

window.kendoEditor_onExecute = kendoEditor_onExecute;
window.setHeightContentEditor = setHeightContentEditor;
window.saveCommentOnButton = saveCommentOnButton;
window.initKendoEditorVerticalOnlyResize = initKendoEditorVerticalOnlyResize;
window.edtBodyFocus = edtBodyFocus;
